/**
 * Date/time formatting helpers for the wiberry Sync Agent panel (#3071).
 *
 * Pure functions, no i18next dependency — the "heute"/"Uhr" wording IS the
 * FR-016.7 copy itself (not a translated label), and keeping this pure keeps
 * it directly unit-testable without a render. Extends this codebase's
 * existing ad hoc `toLocaleDateString()` precedent
 * (`IntegrationAgentImports.tsx`) with the same-day relative form FR-016.7's
 * "Zuletzt aktualisiert: heute, 14:00 Uhr" needs.
 */

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

/**
 * Absolute German date + time, e.g. "11.08.2026, 14:00 Uhr". `—` for a
 * null/invalid value. Built manually (not `toLocaleDateString('de-DE')`,
 * which omits the zero-padding this format wants and whose exact output can
 * depend on the host ICU build) so the result is deterministic across Node
 * versions and CI/local environments.
 */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())} Uhr`;
}

/**
 * "heute, 14:00 Uhr" when `value` falls on the same calendar day as `now`
 * (defaults to the real current time), otherwise the absolute
 * `formatDateTime` form. `now` is an explicit parameter so the same-day
 * branch is deterministically testable.
 */
export function formatFreshness(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return `heute, ${pad(d.getHours())}:${pad(d.getMinutes())} Uhr`;
  }
  return formatDateTime(value);
}

/**
 * Transferred volume as "512 B" / "2 KB" / "2,5 MB" / "3 GB" (S9).
 *
 * The value is the sum of the per-batch **compressed** byte counts the gateway
 * records on a run's report, i.e. what actually crossed the wire — the UI labels
 * it transferred volume rather than implying a database size.
 *
 * 1024-based with KB/MB/GB labels, matching what a farmer's own file manager
 * shows rather than the decimal convention, and clamped at GB: a farm sync
 * moving more than a terabyte compressed is a defect, and "2048 GB" is a more
 * legible alarm than "2 TB".
 *
 * `null`/`undefined` render as `—`, never "0 B" — an in-flight run has moved an
 * unknown amount, not nothing, and "0 B" beside a running sync reads as a broken
 * integration. A genuine `0` DOES render as "0 B": a completed run that found no
 * changes is a real and reassuring answer.
 */
export function formatBytes(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  // `bytes_synced` is summed out of free-form JSONB, so a nonsense value must
  // cost one dash rather than putting NaN on the settings page.
  if (!Number.isFinite(value) || value < 0) return '—';
  if (value < 1024) return `${Math.round(value)} B`;

  const units = ['KB', 'MB', 'GB'];
  let size = value / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  // One decimal place, German comma, and a trailing ",0" dropped so a clean
  // 5 MB does not render as "5,0 MB".
  const rounded = Math.round(size * 10) / 10;
  const text = Number.isInteger(rounded) ? `${rounded}` : `${rounded}`.replace('.', ',');
  return `${text} ${units[unit]}`;
}
